import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import 'dayjs/locale/en-gb';
import { employeesApi } from '../../../../services/api';
import { EmployeeAvatar } from '../../../common/EmployeeAvatar';
import { formatDateOnly } from '../../../../utils/dateOnly';

/**
 * ADR-101: Management info history viewer.
 *
 * Rendered from the Management Information section for users holding the
 * ManagementHistory ability. Two modes:
 * - Timeline (phase 2): merged intervals from GET /employees/:id/management-history
 * - Pick a date (phase 1): the ADR-099 asOfDate endpoint, pinned to one date
 *
 * Provenance comes from the resolvers' `resolvedVia` contract; `live_fallback`
 * marks answers where no history covers the date (pre-backfill approximation).
 */

const TABS = {
  TIMELINE: 0,
  AS_OF: 1,
};

// resolvedVia -> user-facing provenance chip (values from ADR-099's
// RESOLVED_VIA contract; live_fallback = no history covers the date)
const RESOLVED_VIA_LABELS = {
  manager_of_record: { label: 'Manager of Record', color: 'primary' },
  team_history: { label: 'Team history', color: 'primary' },
  bu_owner_history: { label: 'BU owner history', color: 'primary' },
  bu_ownership_history: { label: 'BU ownership history', color: 'primary' },
  // ADR-105: provenance of the project -> BU edge at the date
  project_bu_history: { label: 'Structure history', color: 'primary' },
  // ADR-127: provenance of the SU -> cluster edge at the date, and of the
  // cluster representative of record (manager_history type='cluster')
  bu_cluster_history: { label: 'Structure history', color: 'primary' },
  cluster_manager_history: { label: 'Cluster history', color: 'primary' },
  live_fallback: { label: 'Live fallback — no history at this date', color: 'warning' },
};

const provenanceChip = (resolvedVia, compact = false) => {
  if (!resolvedVia) return null;
  const config = RESOLVED_VIA_LABELS[resolvedVia] || { label: resolvedVia, color: 'default' };
  return (
    <Chip
      label={compact && resolvedVia === 'live_fallback' ? 'Live fallback' : config.label}
      color={config.color}
      size="small"
      variant="outlined"
    />
  );
};

// ADR-197: interval bounds are date-only — the stored calendar day
const formatDay = (isoDate) => formatDateOnly(isoDate) || null;

const PersonCell = ({ person }) => (
  person ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <EmployeeAvatar
        employee={{ fullName: person.fullName, azureId: person.azureId }}
        size={28}
      />
      <Typography variant="body2">{person.fullName}</Typography>
    </Box>
  ) : (
    <Typography variant="body2" color="text.secondary">—</Typography>
  )
);

const PersonRow = ({ label, person, resolvedVia }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
    <Box sx={{ minWidth: 170 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Box>
    <PersonCell person={person} />
    <Box sx={{ marginLeft: 'auto' }}>{provenanceChip(resolvedVia)}</Box>
  </Box>
);

// ADR-105: same row shape for the non-person facts of the allocation chain.
const FactRow = ({ label, value, resolvedVia }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
    <Box sx={{ minWidth: 170 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Box>
    {value ? (
      <Typography variant="body2">{value}</Typography>
    ) : (
      <Typography variant="body2" color="text.secondary">—</Typography>
    )}
    <Box sx={{ marginLeft: 'auto' }}>{provenanceChip(resolvedVia)}</Box>
  </Box>
);

/**
 * ADR-105: the evidence the two answers above were resolved from — which FT
 * project the individual was on that day, which BU it sat in AT THAT DATE, and
 * who ran that BU then. Each provenance is marked independently: the BU edge
 * and the BU's director come from different history tables and can legitimately
 * disagree about coverage (ADR-105 E10).
 */
const AllocationSection = ({ ftAllocation, budgetDirectorUnit, myExecCluster, myExec }) => {
  if (!ftAllocation) {
    return (
      <Alert severity="info" sx={{ mt: 1.5 }}>
        No full-time allocation on this date — which is why there may be no
        Budget Director or My Exec to show.
      </Alert>
    );
  }

  const businessUnit = ftAllocation.businessUnit;
  const escalated = Boolean(
    budgetDirectorUnit && businessUnit && budgetDirectorUnit.id !== businessUnit.id
  );
  // ADR-127: the cluster the SU sat in at T, and whether My Exec escalated to a parent cluster
  const cluster = ftAllocation.cluster || null;
  const execEscalated = Boolean(
    myExecCluster && cluster && myExecCluster.id !== cluster.id
  );

  const projectLabel = ftAllocation.project
    ? [ftAllocation.project.name, ftAllocation.project.code && `(${ftAllocation.project.code})`]
      .filter(Boolean).join(' ')
    : null;

  return (
    <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
        Allocation on this date
      </Typography>

      <FactRow label="FT project" value={projectLabel} />
      <FactRow label="Team" value={ftAllocation.team?.name} />
      <FactRow
        label="Project BU"
        value={businessUnit?.name}
        resolvedVia={businessUnit?.resolvedVia}
      />
      <PersonRow
        label="BU director"
        person={businessUnit?.owner}
        resolvedVia={businessUnit?.owner?.resolvedVia}
      />

      {escalated && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {businessUnit?.name
            ? `${businessUnit.name} had no director on this date — the Budget Director above comes from ${budgetDirectorUnit.name || 'a parent unit'}.`
            : `The Budget Director above comes from ${budgetDirectorUnit.name || 'a parent unit'}.`}
        </Alert>
      )}

      {/* ADR-127: which cluster the SU belonged to at T, and who represented it */}
      <FactRow
        label="Cluster"
        value={cluster?.name}
        resolvedVia={cluster?.resolvedVia}
      />
      <PersonRow
        label="Cluster representative"
        person={execEscalated ? null : myExec}
        resolvedVia={execEscalated ? null : myExec?.resolvedVia}
      />

      {execEscalated && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {cluster?.name
            ? `${cluster.name} had no representative on this date — the My Exec above comes from ${myExecCluster.name || 'a parent cluster'}.`
            : `The My Exec above comes from ${myExecCluster.name || 'a parent cluster'}.`}
        </Alert>
      )}
    </Box>
  );
};

const ManagementHistoryDialog = ({ open, onClose, employeeId, employeeName }) => {
  const [activeTab, setActiveTab] = useState(TABS.TIMELINE);

  // Timeline state
  const [timeline, setTimeline] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(null);

  // As-of state
  const [selectedDate, setSelectedDate] = useState(null);
  const [asOfLoading, setAsOfLoading] = useState(false);
  const [asOfError, setAsOfError] = useState(null);
  const [asOfResult, setAsOfResult] = useState(null);

  const fetchTimeline = useCallback(async () => {
    setTimelineLoading(true);
    setTimelineError(null);
    try {
      const response = await employeesApi.getManagementHistory(employeeId);
      setTimeline(response);
    } catch (err) {
      setTimelineError(err.message || 'Failed to load management info history');
      setTimeline(null);
    } finally {
      setTimelineLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (open && activeTab === TABS.TIMELINE && !timeline && !timelineLoading && !timelineError) {
      fetchTimeline();
    }
  }, [open, activeTab, timeline, timelineLoading, timelineError, fetchTimeline]);

  const fetchAsOf = async (dateValue) => {
    setAsOfLoading(true);
    setAsOfError(null);
    try {
      const response = await employeesApi.getEmployeeAsOf(
        employeeId,
        dateValue.format('YYYY-MM-DD')
      );
      setAsOfResult({
        directManager: response.directManager || null,
        budgetDirector: response.budgetDirector || null,
        myExec: response.myExec || null, // ADR-127
        asOf: response.asOf || null,
      });
    } catch (err) {
      setAsOfError(err.message || 'Failed to load management info for this date');
      setAsOfResult(null);
    } finally {
      setAsOfLoading(false);
    }
  };

  const handleDateChange = (newValue) => {
    setSelectedDate(newValue);
    if (newValue && newValue.isValid()) {
      fetchAsOf(newValue);
    }
  };

  const handleClose = () => {
    setActiveTab(TABS.TIMELINE);
    setTimeline(null);
    setTimelineError(null);
    setTimelineLoading(false);
    setSelectedDate(null);
    setAsOfResult(null);
    setAsOfError(null);
    setAsOfLoading(false);
    onClose();
  };

  const asOfResolvedVia = asOfResult?.asOf?.resolvedVia || {};
  const asOfFtAllocation = asOfResult?.asOf?.ftAllocation ?? null;
  // ADR-105: the allocation chain carries its own provenance, so the
  // approximation notice has to consider it too.
  const asOfHasLiveFallback = [
    ...Object.values(asOfResolvedVia),
    asOfFtAllocation?.businessUnit?.resolvedVia,
    asOfFtAllocation?.businessUnit?.owner?.resolvedVia,
    asOfFtAllocation?.cluster?.resolvedVia // ADR-127
  ].includes('live_fallback');
  const timelineHasLiveFallback = (timeline?.intervals || []).some(
    (interval) => Object.values(interval.resolvedVia || {}).includes('live_fallback')
  );

  const renderTimeline = () => (
    <Box sx={{ mt: 2 }}>
      {timelineLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {timelineError && (
        <Alert severity="error">{timelineError}</Alert>
      )}

      {!timelineLoading && !timelineError && timeline && (
        timeline.intervals.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No management info could be resolved for this individual&apos;s history.
          </Typography>
        ) : (
          <>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell>Direct Representative</TableCell>
                  <TableCell>Budget Director</TableCell>
                  <TableCell>My Exec</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...timeline.intervals].reverse().map((interval) => (
                  <TableRow key={interval.from}>
                    <TableCell sx={{ whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                      <Typography variant="body2">
                        {formatDay(interval.from)} — {formatDay(interval.to) || 'Present'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <PersonCell person={interval.directManager} />
                      <Box sx={{ mt: 0.5 }}>
                        {provenanceChip(interval.resolvedVia?.directManager, true)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <PersonCell person={interval.budgetDirector} />
                      <Box sx={{ mt: 0.5 }}>
                        {provenanceChip(interval.resolvedVia?.budgetDirector, true)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <PersonCell person={interval.myExec} />
                      <Box sx={{ mt: 0.5 }}>
                        {provenanceChip(interval.resolvedVia?.myExec, true)}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {timeline.truncated && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                This history was truncated — the range contains more change
                points than can be shown. Narrow the range via the as-of view.
              </Alert>
            )}
            {timelineHasLiveFallback && (
              <Alert severity="info" sx={{ mt: 1.5 }}>
                Periods marked &quot;Live fallback&quot; predate history
                coverage — the value shown is an approximation based on
                current data.
              </Alert>
            )}
          </>
        )
      )}
    </Box>
  );

  const renderAsOf = () => (
    <Box sx={{ mt: 2 }}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
        <DatePicker
          label="As of date"
          value={selectedDate}
          onChange={handleDateChange}
          disableFuture
          slotProps={{ textField: { fullWidth: true, size: 'small' } }}
        />
      </LocalizationProvider>

      {asOfLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {asOfError && (
        <Alert severity="error" sx={{ mt: 2 }}>{asOfError}</Alert>
      )}

      {!asOfLoading && !asOfError && asOfResult && (
        <Box sx={{ mt: 2 }}>
          <PersonRow
            label="Direct Representative"
            person={asOfResult.directManager}
            resolvedVia={asOfResolvedVia.directManager}
          />
          <PersonRow
            label="Budget Director"
            person={asOfResult.budgetDirector}
            resolvedVia={asOfResolvedVia.budgetDirector}
          />
          <PersonRow
            label="My Exec"
            person={asOfResult.myExec}
            resolvedVia={asOfResolvedVia.myExec}
          />
          <AllocationSection
            ftAllocation={asOfFtAllocation}
            budgetDirectorUnit={asOfResult.asOf?.budgetDirectorUnit}
            myExecCluster={asOfResult.asOf?.myExecCluster}
            myExec={asOfResult.myExec
              ? { ...asOfResult.myExec, resolvedVia: asOfResolvedVia.myExec }
              : null}
          />
          {asOfHasLiveFallback && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              No history covers this date for the flagged field — the current
              live value is shown as an approximation.
            </Alert>
          )}
        </Box>
      )}

      {!asOfLoading && !asOfError && !asOfResult && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Pick a date to see who this individual&apos;s Direct Representative,
          Budget Director and My Exec were on that day, and the allocation they
          were resolved from.
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Management Info History
        {employeeName ? ` — ${employeeName}` : ''}
      </DialogTitle>
      <DialogContent>
        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue)}
        >
          <Tab label="Timeline" />
          <Tab label="Pick a date" />
        </Tabs>
        {activeTab === TABS.TIMELINE ? renderTimeline() : renderAsOf()}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManagementHistoryDialog;
